import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Palette, Upload, Trash2 } from "lucide-react";
import { CtaButton, MonoLabel } from "./kit";
import { BrandExtract } from "./BrandExtract";
import { FontPicker } from "./FontPicker";
import api, { formatApiErrorDetail } from "../lib/api";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const LEGACY_MAP = { elegant: ["Playfair Display", "Inter"], bold: ["Archivo Black", "Inter"], friendly: ["Poppins", "Poppins"], minimal: ["Inter", "Inter"] };

const ColorField = ({ label, value, onChange, testid }) => (
  <div>
    <p className="text-[10px] font-bold tracking-[0.1em] text-[#9A9591] uppercase mb-2">{label}</p>
    <div className="flex items-center gap-2.5">
      <span className="rounded-xl border border-[#EAE5DB] bg-white p-1 shadow-sm">
        <input
          type="color"
          value={HEX_RE.test(value) ? value : "#e7a5b0"}
          onChange={(e) => onChange(e.target.value)}
          className="w-11 h-11 rounded-lg cursor-pointer border-0 bg-transparent p-0"
          data-testid={`${testid}-picker`}
        />
      </span>
      <input
        className="field-input !py-2 !text-[13px] w-[110px] tabular-nums"
        value={value}
        maxLength={7}
        placeholder="#RRGGBB"
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  </div>
);

export const BrandKitCard = ({ kit = {}, onSaved }) => {
  const [primary, setPrimary] = useState(kit.primary_color || "");
  const [secondary, setSecondary] = useState(kit.secondary_color || "");
  const legacy = LEGACY_MAP[kit.typography] || [];
  const [primaryFont, setPrimaryFont] = useState(kit.primary_font || legacy[0] || "");
  const [secondaryFont, setSecondaryFont] = useState(kit.secondary_font || (kit.primary_font ? "" : legacy[1] === "Inter" ? "" : legacy[1]) || "");
  const [fonts, setFonts] = useState([]);
  const [moods, setMoods] = useState(kit.mood_keywords || []);
  const [moodInput, setMoodInput] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/fonts").then(({ data }) => setFonts(data.fonts || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!kit.has_logo) return;
    let url;
    api.get("/brand-kit/logo", { responseType: "blob" })
      .then(({ data }) => { url = URL.createObjectURL(data); setLogoUrl(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addMood = () => {
    const v = moodInput.trim();
    if (!v) return;
    if (moods.length >= 8) { toast.error("Max 8 mood keywords."); return; }
    if (!moods.includes(v)) setMoods([...moods, v]);
    setMoodInput("");
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/brand-kit/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(URL.createObjectURL(file));
      if (data.colors?.length && !primary) {
        setPrimary(data.colors[0]);
        if (data.colors[1] && !secondary) setSecondary(data.colors[1]);
        toast("Colors extracted from your logo.", { description: "Tweak them below if needed, then save." });
      } else {
        toast("Logo uploaded.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteLogo = async () => {
    try {
      await api.delete("/brand-kit/logo");
      setLogoUrl(null);
      toast("Logo removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const save = async () => {
    if (!primaryFont) { toast.error("Pick a primary font — it drives all your headings."); return; }
    for (const [label, v] of [["Primary", primary], ["Secondary", secondary]]) {
      if (v && !HEX_RE.test(v)) { toast.error(`${label} color must be a #RRGGBB hex code.`); return; }
    }
    const body = { mood_keywords: moods };
    if (primary) body.primary_color = primary;
    if (secondary) body.secondary_color = secondary;
    if (primaryFont) body.primary_font = primaryFont;
    body.secondary_font = secondaryFont || "";
    setBusy(true);
    try {
      await api.put("/brand-kit", body);
      toast("Brand Kit saved.", { description: "Every new creative will come out on-brand." });
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-soft mt-5 p-6 sm:p-8" data-testid="settings-brand-kit-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,.55), rgba(255,255,255,0) 72%), linear-gradient(135deg, #F1795A, #D24E2A)",
              boxShadow: "0 8px 18px -6px rgba(210,78,42,.5)",
            }}
          >
            <Palette size={19} strokeWidth={1.8} className="text-white" style={{ filter: "drop-shadow(0 2px 3px rgba(20,15,10,.25))" }} />
          </div>
          <div>
            <MonoLabel>BRAND KIT</MonoLabel>
            <p className="text-[12.5px] text-[#54505B] mt-0.5">Logo, colors and type — applied to every creative in the Creative Studio.</p>
          </div>
        </div>
      </div>

      <div className="mt-6 pb-6 border-b border-[#F3EFE7]">
        <BrandExtract onDone={onSaved} />
      </div>

      <div className="grid sm:grid-cols-[160px_1fr] gap-6 mt-6">
        <div>
          <p className="font-mono-j text-[9.5px] tracking-[0.12em] text-[#9A9591] uppercase mb-1.5">Logo</p>
          {logoUrl ? (
            <div className="relative w-[140px]">
              <img src={logoUrl} alt="Brand logo" className="w-[140px] h-[140px] object-contain rounded-xl border border-[#EAE5DB] bg-white p-2" data-testid="bk-logo-preview" />
              <button
                onClick={deleteLogo}
                className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#181621] text-white flex items-center justify-center hover:bg-[#3a3b45]"
                data-testid="bk-logo-delete"
              >
                <Trash2 size={13} strokeWidth={1.8} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="w-[140px] h-[140px] rounded-xl border-2 border-dashed border-[#E7E2D6] bg-white flex flex-col items-center justify-center gap-2 text-[#54505B] hover:border-[#181621] transition-colors"
              data-testid="bk-logo-upload-btn"
            >
              <Upload size={18} strokeWidth={1.8} />
              <span className="text-[11.5px] font-semibold px-3 text-center leading-snug">PNG with transparency, under 4 MB</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="bk-logo-input" />
        </div>

        <div className="space-y-5">
          <div className="flex flex-wrap gap-6">
            <ColorField label="Primary color" value={primary} onChange={setPrimary} testid="bk-primary-color" />
            <ColorField label="Secondary color" value={secondary} onChange={setSecondary} testid="bk-secondary-color" />
          </div>

          <div>
            <p className="text-[10px] font-bold tracking-[0.1em] text-[#9A9591] uppercase mb-2">Typography</p>
            <div className="flex flex-wrap gap-5">
              <FontPicker label="Primary font" required value={primaryFont} onChange={setPrimaryFont} fonts={fonts} placeholder="Headings & display…" testid="bk-primary-font" />
              <FontPicker label="Secondary font" value={secondaryFont} onChange={setSecondaryFont} fonts={fonts} placeholder="Body — neutral default (Inter)" testid="bk-secondary-font" />
            </div>
            <p className="text-[11px] text-[#9A9591] mt-1.5">Primary drives headings everywhere (carousels, creatives, landing pages). No secondary = clean Inter for body.</p>
          </div>

          <div>
            <p className="text-[10px] font-bold tracking-[0.1em] text-[#9A9591] uppercase mb-2">Mood keywords <span className="normal-case tracking-normal">(max 8)</span></p>
            <div className="flex flex-wrap gap-2 items-center">
              {moods.map((m) => (
                <span key={m} className="inline-flex items-center gap-1.5 rounded-full border border-[#EAE5DB] bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.05em] text-[#54505B]" data-testid={`bk-mood-chip-${m.replace(/\s+/g, "-").toLowerCase()}`}>
                  {m.toUpperCase()}
                  <button onClick={() => setMoods(moods.filter((x) => x !== m))} className="text-[#9A9591] hover:text-[#181621]" data-testid={`bk-mood-remove-${m.replace(/\s+/g, "-").toLowerCase()}`}>×</button>
                </span>
              ))}
              <input
                className="field-input !py-1.5 !text-[13px] w-[170px]"
                placeholder='e.g. "premium", press Enter'
                value={moodInput}
                maxLength={40}
                onChange={(e) => setMoodInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addMood(); } }}
                onBlur={addMood}
                data-testid="bk-mood-input"
              />
            </div>
          </div>

          <CtaButton className="!py-2.5 !px-5 text-[13.5px]" onClick={save} disabled={busy} data-testid="bk-save-btn">
            Save Brand Kit
          </CtaButton>
        </div>
      </div>
    </div>
  );
};
